import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { AuthFirebaseFunctionsService } from '@principle-theorem/ng-auth';
import { NgPrincipleModule } from '@principle-theorem/ng-principle';
import { PartnerService } from '@principle-theorem/ng-principle-shared';
import {
  BreadcrumbsStore,
  type IBreadcrumb,
} from '@principle-theorem/ng-shared';
import {
  IPartner,
  type IUser,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  findProp,
  resolveProp$,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, race, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'pr-partner-user-edit',
  templateUrl: './partner-user-edit.component.html',
  styleUrls: ['./partner-user-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NgPrincipleModule],
})
export class PartnerUserEditComponent {
  breadcrumbs$: Observable<IBreadcrumb[]>;
  user$: Observable<WithRef<IUser>>;
  partner$: Observable<WithRef<IPartner>>;

  constructor(
    private _route: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private _functions: AuthFirebaseFunctionsService,
    breadcrumbs: BreadcrumbsStore,
    partnerService: PartnerService
  ) {
    this.partner$ = race(
      partnerService.partner$.pipe(filterUndefined()),
      this._route.data.pipe(
        findProp<WithRef<IPartner>>('partner'),
        filterUndefined()
      )
    );

    this.user$ = resolveProp$<WithRef<IUser>>(this._route.data, 'user');

    this.breadcrumbs$ = combineLatest([this.partner$, this.user$]).pipe(
      map(([partner, user]) => [
        { label: partner.name, path: '../../' },
        { label: 'Users' },
        { label: user.name },
      ])
    );

    breadcrumbs.setBreadcrumbs(this.breadcrumbs$);
  }

  async invite(user: WithRef<IUser>): Promise<void> {
    const partner = await snapshot(this.partner$);
    await this._functions.sendPartnerInviteEmail(partner, user);
    this._snackBar.open('Invitation Email Sent');
  }
}
